import React, { useState } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Row,
    Col,
    FormGroup,
    Label,
    Input,
} from "reactstrap";
import Select from "react-select";
import { InstituicaoType } from "../../context/hooks/useInstituicao";
import { ContratoType } from "../../context/hooks/useContrato";
import { CursoType } from "../../context/hooks/useCurso";

export const RELATORIO_ALUNO_FIELDS = [
    { label: "Sequência", value: "AlunoId" },
    { label: "Código de referência", value: "ReferenceCode" },
    { label: "Nome", value: "AlunoNome" },
    { label: "Email", value: "Email" },
    { label: "Curso", value: "CursoNome" },
    { label: "CPF", value: "CPF" },
    { label: "Endereço", value: "Address" },
    { label: "Nº", value: "AddressNumber" },
    { label: "Bairro", value: "Neighborhood" },
    { label: "Cidade", value: "City" },
    { label: "Estado", value: "State" },
    { label: "CEP", value: "ZipCode" },
    { label: "Fixo", value: "Landline" },
    { label: "Tel 01", value: "Telephone_01" },
    { label: "Tel 02", value: "Telephone_02" },
    { label: "Tel 03", value: "Telephone_03" },
    { label: "Tel 04", value: "Telephone_04" },
    { label: "Tel 05", value: "Telephone_05" },
    { label: "Tel 06", value: "Telephone_06" },
    { label: "Facebook", value: "Facebook" },
    { label: "Instagram", value: "Instagram" },
    { label: "Manter as fotos?", value: "KeepThePhotos" },
    { label: "Possui álbum?", value: "HasAlbum" },
    { label: "Nº de fotos", value: "NumberOfPhotos" },
    { label: "Contrato", value: "Contrato" },
    { label: "Instituição", value: "Instituicao" },
    { label: "Data de Cadastro", value: "DataCadastro" },
];

export type PeriodoRelatorio = "" | "diario" | "mensal" | "trimestral" | "semestral" | "anual";

interface Props {
    isOpen: boolean;
    toggle: () => void;
    listInstituicao: InstituicaoType[];
    listContrato: ContratoType[];
    listCurso?: CursoType[];
    initialFilters: {
        contratoId?: number;
        instituicaoId?: number;
        city?: string;
        state?: string;
        region?: string;
        nomeFormando?: string;
        periodo?: PeriodoRelatorio;
        dataReferencia?: string;
    };
    onGerarRelatorio: (params: {
        contratoId?: number;
        instituicaoId?: number;
        city?: string;
        state?: string;
        region?: string;
        nomeFormando?: string;
        periodo: PeriodoRelatorio;
        dataReferencia?: string;
        camposSelecionados: string[];
    }) => void;
}

export const ExportAlunoRelatorioModal: React.FC<Props> = ({
    isOpen,
    toggle,
    listInstituicao,
    listContrato,
    initialFilters,
    onGerarRelatorio,
}) => {
    const [selectedFields, setSelectedFields] = useState<string[]>([
        "AlunoId",
        "AlunoNome",
        "Email",
        "CursoNome",
        "Contrato",
        "Instituicao",
    ]);
    const [contratoId, setContratoId] = useState<number | undefined>(initialFilters.contratoId);
    const [instituicaoId, setInstituicaoId] = useState<number | undefined>(initialFilters.instituicaoId);
    const [city, setCity] = useState<string>(initialFilters.city || "");
    const [state, setState] = useState<string>(initialFilters.state || "");
    const [region, setRegion] = useState<string>(initialFilters.region || "");
    const [nomeFormando, setNomeFormando] = useState<string>(initialFilters.nomeFormando || "");
    const [periodo, setPeriodo] = useState<PeriodoRelatorio>(initialFilters.periodo || "");
    const [dataReferencia, setDataReferencia] = useState<string>(initialFilters.dataReferencia || "");

    React.useEffect(() => {
        if (isOpen) {
            setContratoId(initialFilters.contratoId || undefined);
            setInstituicaoId(initialFilters.instituicaoId || undefined);
            setCity(initialFilters.city || "");
            setState(initialFilters.state || "");
            setRegion(initialFilters.region || "");
            setNomeFormando(initialFilters.nomeFormando || "");
            setPeriodo(initialFilters.periodo || "");
            setDataReferencia(initialFilters.dataReferencia || "");
        }
    }, [isOpen, initialFilters]);

    const handleFieldSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
        const fieldValue = e.target.value;
        if (!fieldValue) return;
        if (!selectedFields.includes(fieldValue)) {
            setSelectedFields([...selectedFields, fieldValue]);
        }
        e.target.value = "";
    };

    const handleRemoveField = (fieldValue: string) => {
        setSelectedFields(selectedFields.filter((f) => f !== fieldValue));
    };

    const handleSelectAllFields = () => {
        setSelectedFields(RELATORIO_ALUNO_FIELDS.map((f) => f.value));
    };

    const handleClearAllFields = () => {
        setSelectedFields([]);
    };

    const handleSubmit = () => {
        if (selectedFields.length === 0) {
            return;
        }
        onGerarRelatorio({
            contratoId: contratoId && contratoId > 0 ? contratoId : undefined,
            instituicaoId: instituicaoId && instituicaoId > 0 ? instituicaoId : undefined,
            city: city.trim() || undefined,
            state: state.trim() || undefined,
            region: region.trim() || undefined,
            nomeFormando: nomeFormando.trim() || undefined,
            periodo,
            dataReferencia: dataReferencia || undefined,
            camposSelecionados: selectedFields,
        });
        toggle();
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" backdrop="static">
            <ModalHeader toggle={toggle}>
                <i className="fa-solid fa-file-pdf mr-2 text-danger"></i>
                Gerar Relatório de Alunos / Formandos
            </ModalHeader>
            <ModalBody>
                <p className="text-muted mb-3" style={{ fontSize: "0.9rem" }}>
                    Configure os filtros e selecione as colunas que devem constar no relatório em PDF.
                </p>

                <div className="p-3 mb-3" style={{ backgroundColor: "#f8f9fe", borderRadius: 8, border: "1px solid #e9ecef" }}>
                    <h5 className="text-primary mb-3">Filtros do Relatório</h5>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Instituição</Label>
                                <Select
                                    placeholder="Todas ou selecione..."
                                    isClearable
                                    options={listInstituicao.map((i) => ({ value: i.id, label: i.name }))}
                                    value={
                                        instituicaoId
                                            ? {
                                                  value: instituicaoId,
                                                  label:
                                                      listInstituicao.find((i) => i.id === instituicaoId)?.name ||
                                                      `Instituição #${instituicaoId}`,
                                              }
                                            : null
                                    }
                                    onChange={(opt) => setInstituicaoId(opt ? (opt as any).value : undefined)}
                                    noOptionsMessage={() => "Nenhuma instituição encontrada"}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Contrato específico</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="number"
                                    placeholder="Todos ou digite o nº (ex: 123)"
                                    value={contratoId || ""}
                                    onChange={(e) =>
                                        setContratoId(e.target.value ? Number(e.target.value) : undefined)
                                    }
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Região</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="select"
                                    value={region}
                                    onChange={(e) => setRegion(e.target.value)}
                                >
                                    <option value="">Todas</option>
                                    <option value="Norte">Norte</option>
                                    <option value="Nordeste">Nordeste</option>
                                    <option value="Centro-Oeste">Centro-Oeste</option>
                                    <option value="Sudeste">Sudeste</option>
                                    <option value="Sul">Sul</option>
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Estado (UF)</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="select"
                                    value={state}
                                    onChange={(e) => setState(e.target.value)}
                                >
                                    <option value="">Todos</option>
                                    {[
                                        "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
                                        "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
                                        "RS", "RO", "RR", "SC", "SP", "SE", "TO",
                                    ].map((uf) => (
                                        <option key={uf} value={uf}>
                                            {uf}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Cidade</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    placeholder="Filtrar por cidade..."
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Nome do Formando</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    placeholder="Filtrar por nome..."
                                    value={nomeFormando}
                                    onChange={(e) => setNomeFormando(e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Período de Cadastro</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="select"
                                    value={periodo}
                                    onChange={(e) => setPeriodo(e.target.value as PeriodoRelatorio)}
                                >
                                    <option value="">Todo o período</option>
                                    <option value="diario">Diário</option>
                                    <option value="mensal">Mensal</option>
                                    <option value="trimestral">Trimestral</option>
                                    <option value="semestral">Semestral</option>
                                    <option value="anual">Anual</option>
                                </Input>
                            </FormGroup>
                        </Col>
                        {periodo && (
                            <Col md="4">
                                <FormGroup>
                                    <Label className="form-control-label">Data de Referência</Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="date"
                                        value={dataReferencia}
                                        onChange={(e) => setDataReferencia(e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                        )}
                    </Row>
                </div>

                <FormGroup>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <Label className="form-control-label font-weight-bold mb-0">
                            Colunas do Relatório ({selectedFields.length} selecionadas)
                        </Label>
                        <div>
                            <Button size="sm" color="link" onClick={handleSelectAllFields} className="p-0 mr-3">
                                Selecionar todas
                            </Button>
                            <Button size="sm" color="link" onClick={handleClearAllFields} className="p-0 text-danger">
                                Limpar seleção
                            </Button>
                        </div>
                    </div>

                    <Input
                        className="form-control-alternative mb-2"
                        type="select"
                        name="field"
                        onChange={handleFieldSelection}
                        defaultValue=""
                    >
                        <option value="">Adicionar campo...</option>
                        {RELATORIO_ALUNO_FIELDS.filter((f) => !selectedFields.includes(f.value)).map((field) => (
                            <option key={field.value} value={field.value}>
                                + {field.label}
                            </option>
                        ))}
                    </Input>

                    <div className="d-flex flex-wrap" style={{ gap: "6px" }}>
                        {selectedFields.map((value) => {
                            const field = RELATORIO_ALUNO_FIELDS.find((f) => f.value === value);
                            return (
                                <span
                                    key={value}
                                    className="badge badge-primary d-inline-flex align-items-center py-2 px-2"
                                    style={{ fontSize: "0.85rem" }}
                                >
                                    {field?.label || value}
                                    <button
                                        type="button"
                                        className="close ml-2 text-white"
                                        style={{ outline: "none", fontSize: "1rem", lineHeight: 1 }}
                                        onClick={() => handleRemoveField(value)}
                                    >
                                        &times;
                                    </button>
                                </span>
                            );
                        })}
                    </div>
                </FormGroup>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>
                    Cancelar
                </Button>
                <Button color="danger" onClick={handleSubmit} disabled={selectedFields.length === 0}>
                    <i className="fa-solid fa-file-pdf mr-2"></i>
                    Gerar Relatório (PDF)
                </Button>
            </ModalFooter>
        </Modal>
    );
};
